import { UISimulation } from '@/components/help/UISimulation';

const STEP_DURATION = 2200;

interface NodeProps {
  label: string;
  state: 'pending' | 'active' | 'done';
}

function Node({ label, state }: NodeProps): JSX.Element {
  const styles =
    state === 'active'
      ? 'border-accent bg-accent/15 text-accent'
      : state === 'done'
        ? 'border-success/60 bg-success/10 text-success'
        : 'border-dash-border bg-dash-surface text-dash-muted';
  return (
    <div className={`rounded-full border px-3 py-1 text-xs font-medium ${styles}`}>
      {label}
    </div>
  );
}

function Arrow({ kind = 'forward' }: { kind?: 'forward' | 'loop' }): JSX.Element {
  if (kind === 'loop') {
    return (
      <span aria-hidden="true" className="text-warning text-xs font-mono">
        ↻
      </span>
    );
  }
  return (
    <span aria-hidden="true" className="text-dash-muted text-xs font-mono">
      →
    </span>
  );
}

function ScoreRow({ score }: { score: number }): JSX.Element {
  const pass = score >= 8;
  return (
    <div className="mt-3 flex items-center gap-2 text-xs">
      <span className="text-[10px] font-mono uppercase tracking-wide text-dash-muted">
        Review score
      </span>
      <span
        className={
          pass
            ? 'rounded bg-success/15 text-success border border-success/40 px-2 py-0.5 text-[11px] font-mono'
            : 'rounded bg-warning/15 text-warning border border-warning/40 px-2 py-0.5 text-[11px] font-mono'
        }
      >
        {score.toFixed(1)} / 10
      </span>
      <span className="text-dash-muted">{pass ? 'pass — complete' : 'below threshold — iterate'}</span>
    </div>
  );
}

function WriteFrame(): JSX.Element {
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-4">
      <div className="text-[10px] font-mono uppercase tracking-wide text-dash-muted mb-3">
        Iteration 1 — Writer
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Node label="Write" state="active" />
        <Arrow />
        <Node label="Review" state="pending" />
        <Arrow />
        <Node label="Score" state="pending" />
        <Arrow kind="loop" />
        <Node label="Iterate" state="pending" />
      </div>
      <div className="mt-3 text-xs text-dash-secondary">
        Claude drafts the article from the approved brief.
      </div>
    </div>
  );
}

function ReviewFrame(): JSX.Element {
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-4">
      <div className="text-[10px] font-mono uppercase tracking-wide text-dash-muted mb-3">
        Iteration 1 — Reviewer
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Node label="Write" state="done" />
        <Arrow />
        <Node label="Review" state="active" />
        <Arrow />
        <Node label="Score" state="pending" />
        <Arrow kind="loop" />
        <Node label="Iterate" state="pending" />
      </div>
      <div className="mt-3 text-xs text-dash-secondary">
        A second Claude persona checks structure, citations, factual flags.
      </div>
    </div>
  );
}

function ScoreFrame(): JSX.Element {
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-4">
      <div className="text-[10px] font-mono uppercase tracking-wide text-dash-muted mb-3">
        Iteration 1 — Score check (fail)
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Node label="Write" state="done" />
        <Arrow />
        <Node label="Review" state="done" />
        <Arrow />
        <Node label="Score" state="active" />
        <Arrow kind="loop" />
        <Node label="Iterate" state="pending" />
      </div>
      <ScoreRow score={6.4} />
    </div>
  );
}

function IterateFrame(): JSX.Element {
  return (
    <div className="rounded-md border border-warning/40 bg-warning/10 p-4">
      <div className="text-[10px] font-mono uppercase tracking-wide text-warning mb-3">
        Iteration 2 — Loop back
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Node label="Write" state="active" />
        <Arrow />
        <Node label="Review" state="pending" />
        <Arrow />
        <Node label="Score" state="pending" />
        <Arrow kind="loop" />
        <Node label="Iterate" state="done" />
      </div>
      <div className="mt-3 text-xs text-dash-secondary">
        Writer revises against reviewer notes. Max 3 iterations.
      </div>
    </div>
  );
}

function CompleteFrame(): JSX.Element {
  return (
    <div className="rounded-md border border-success/40 bg-success/10 p-4">
      <div className="text-[10px] font-mono uppercase tracking-wide text-success mb-3">
        Iteration 2 — Complete
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Node label="Write" state="done" />
        <Arrow />
        <Node label="Review" state="done" />
        <Arrow />
        <Node label="Score" state="done" />
        <Arrow />
        <Node label="Draft" state="done" />
      </div>
      <ScoreRow score={8.6} />
    </div>
  );
}

export function PipelineLoopSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        { frame: <WriteFrame />, label: 'Writer drafts', duration: STEP_DURATION },
        { frame: <ReviewFrame />, label: 'Reviewer critiques', duration: STEP_DURATION },
        { frame: <ScoreFrame />, label: 'Score check', duration: STEP_DURATION },
        { frame: <IterateFrame />, label: 'Loop back to writer', duration: STEP_DURATION },
        { frame: <CompleteFrame />, label: 'Threshold met — draft', duration: STEP_DURATION },
      ]}
    />
  );
}
